import { useCallback, useEffect, useState } from "react";
import { findIndex } from "lodash";
import { useHistory, useParams } from "react-router-dom";
import { coursePaths } from "@/react/helpers/urlHelpers";
import { orderLessons } from "@/react/hooks/courses/useOrderedLessons";
import type { Space } from "@/react/types";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import type { LessonParams } from "./useCompleteLessonMutation";

export type OrderedLesson = CoursesLesson & { section_id: string | number };
interface NavigateToNextLessonProps {
  refetchSpace: () => any;
  space?: Space;
}
export const useNavigateToNextLesson: ({
  space,
  refetchSpace,
}: NavigateToNextLessonProps) => {
  navigateToNextLesson: () => void;
  nextLesson: OrderedLesson | undefined;
  shouldShowCongratulationsModal: boolean;
} = ({ space, refetchSpace }) => {
  const history = useHistory();
  const params = useParams<LessonParams>();
  const [lessonToGoTo, setLessonToGoTo] = useState<OrderedLesson | undefined>(
    undefined,
  );
  const [shouldShowCongratulationsModal, setShouldShowCongratulationsModal] =
    useState<boolean>(false);

  const calculateLessonToGoTo = useCallback(async () => {
    const response = await refetchSpace();
    const orderedLessons: OrderedLesson[] = orderLessons(
      response.data?.course_sections,
    );
    const currentLessonIndex = orderedLessons.findIndex(
      lesson => lesson.id == parseInt(params.lessonId),
    );
    const nextIncompleteLessonInOrder = findIndex(
      orderedLessons,
      lesson => !lesson.completed,
      currentLessonIndex + 1,
    );
    const firstIncompleteLessonIndex = orderedLessons.findIndex(
      lesson => !lesson.completed && lesson.id != parseInt(params.lessonId),
    );
    const nextLesson =
      orderedLessons[nextIncompleteLessonInOrder] ||
      orderedLessons[firstIncompleteLessonIndex];
    setShouldShowCongratulationsModal(!nextLesson);
    setLessonToGoTo(nextLesson);
    return nextLesson;
  }, [params.lessonId, refetchSpace]);

  useEffect(() => {
    void calculateLessonToGoTo();
  }, [calculateLessonToGoTo]);

  const navigateToNextLesson = (lesson?: OrderedLesson | undefined) => {
    if (!space) return;

    const nextLesson = lesson || lessonToGoTo;

    if (nextLesson) {
      history.push(
        coursePaths.showLesson({
          lessonId: nextLesson.id,
          sectionId: nextLesson.section_id,
          spaceSlug: space.slug,
        }),
      );
    }
  };

  return {
    nextLesson: lessonToGoTo,
    shouldShowCongratulationsModal,
    navigateToNextLesson,
  };
};
